import { Config, Helper } from 'config';
import { forEach, find, map, keys, compact, every, invokeMap, join, uniqueId } from 'lodash';

import { forwardPropertiesFrom, resolve, map$, clone, cloneDeep, Magnitude } from 'gear';

import { Xml } from '../../util';
import { Point, Matrix, Bounds } from '../../geometry';
import { Color, Brush } from '../../style';

import { Figure } from '../figure';

import { AnchorGenerator } from '../anchor';
import { Shape } from '../shape';
import { PowerClipContent } from '../power-clip';
import { _toPrimitives } from '../group';

import { TextLayout } from './text-layout';

import { Font } from './font';

let defNum = function (v, d) {
  return isFinite(v) ? v : d;
};

export const Monogram = Figure.Monogram = Figure.extend( {
  typeName: 'Monogram',

  type: 'Monogram',

  Properties: {
    line       : { type: 'string'   , def: 'ABC'                    , manualParsing: false, onChange() { this.invalidate(); } },
    frameSystem: { type: 'string'   , def: 'Circle'                 , manualParsing: false, onChange() { this.invalidate(); } },
    fontStyle  : { type: 'string'   , def: 'Standard'               , manualParsing: false, onChange() { this.invalidate(); } },
    size       : { type: 'magnitude', def:new Magnitude(54.2, 'mm') , manualParsing: false, onChange() { this.invalidate(); } },

    // Used in output figure polyregions and text figures from multi part text, to reference PowerClipContent in the input figure
    // A PowerClipLink has an extra matrix so we are able to transform the content without cloning it
    powerClipLink: { type: 'PowerClipLink', def: null, owned: true, manualParsing: true }
  },

  get isText() { return true; },
  get isMultiPartText() { return false; },
  get isSimpleText() { return !this.isMultiPart && !this.isTypeset; },
  get isTypeset() { return this.text.length > 1; },
  get isMultiPart() { return this.isMultiPartText; },
  get isSimple() { return this.isSimpleText; },
  get isEmpty() { return this._effectiveText.length == 0; },
  get font() { return this._effectiveFont; },
  get fontName() {
    let lLine = this.line.substr(0,3);
    return `Stahls Mgram ${this.frameSystem} ${this.fontStyle} ${lLine.length} LTR`;
  },
  
  containsLinkedResources() {
    return true;
  },

  drawForClipping( context, transform ) {
    this.outputFigure.drawForClipping(context, transform);
  },

  // [TODO][PROP]
  powerClip(v) {
    const powerClipContents = this._powerClipContents;
    if (v === undefined) {
      return powerClipContents[0];
    }
    powerClipContents.set(v);
    this.powerClipId = v._id;
    return this;
  },

  addPowerClipContents(v) {
    const id = this.powerClipId || uniqueId("PC");
    const powerClipContents = this._powerClipContents;
    const pccN = PowerClipContent.createForFigure(id, v, this);

    let pcc = find(powerClipContents, '_id', id);
    if (!pcc) {
      powerClipContents._set(pccN);
      this._properties._powerClipId = id;
      pcc = pccN;

      this.invalidate();
    }
    else {
      pcc._figures._add(pccN._figures);

      if (this.usingPowerClipLink()) {
        this.invalidateView();
      }
      else {
        this.invalidate();
      }
    }
  },

  buildXml(parentNode) { Xml.addObject(parentNode, this, 'Monogram'); },
  //setupXml(node) { this.callParent(node); },
  createEmptyOutput() {
    return Shape.CreateRectangle({ center: this.pin, width: this.size, height: this.size, brush: Brush.empty });
  },

  processesAreSupported() {
    return this._processes.canProcess(this);
  },
  supportedLocalMultiPart() {
    return true;
  },
  canBeProcessedLocally() {
    return Config.localText &&
      !Helper.isIE_let(9) &&
      this.processesAreSupported() &&
      this.supportedLocalMultiPart() &&
      !this.needsCacheToRender();
  },

  concreteFontSize_: function () {
    const preScale = this._computedPreScale;
    const matrix = this.globalMatrix_();
    const urm = Matrix.m(Matrix.rotate(-matrix.angle()), matrix);
    // console.log( preScale.x + ' ' + ( preScale.x * urm.s00 ) + ' | ' + preScale.y + ' ' + ( preScale.y * urm.s11 ) );
    return this.fontSize * Math.min(preScale.x * Math.abs(urm.s00), preScale.y * Math.abs(urm.s11));
  },

  fontDataScale_() {
    // assert( this._fontData );
    return this._fontDataScale;
  },

  fontData$() {
    console.log("  fontData$");
    const me = this;
    const manager = Config.GlobalResourceManager;
    return manager.loadFontData$(me._effectiveFont, me._effectiveText,'~').then(fontData => {
      me._fontData = fontData;
      // Cache information, as a precaution in case somebody changes the model
      // while processing. We need to review when we are unlocking canvas to avoid
      // actions to interfere one another
      me._fontDataScale = fontData.scaleForSize(me.size, true);
      me._fontDataLines = [me._effectiveText];

      console.log("    fontDataScale: " + me._fontDataScale );

      return fontData;
    });
  },

  inputPrimitiveFigures$() {
    const _this = this;

    
    console.log("  inputPrimitiveFigures$");
    
    console.log("    Size: " + this.size);

    console.log("    Original Line: " + this.line);

    var lLine = this.line.substr(0,3);

    var lEffectiveChars = [] ;

    switch ( lLine.length )
    {
      case 1 :
        lEffectiveChars.push( lLine.charAt(0).toUpperCase() ) ;
        break ;
      case 2 :
        lEffectiveChars.push( lLine.charAt(0).toUpperCase() ) ;
        lEffectiveChars.push( lLine.charAt(1).toLowerCase() ) ;
        break ;
      case 3 :
        var lMiddleUnicode         = lLine.charAt(1).toUpperCase().charCodeAt(0) ;
        var lAdjustedMiddleUnicode = lMiddleUnicode - 0x41 + 0x2500 ;
        var lMiddleChar            = String.fromCharCode(lAdjustedMiddleUnicode);

        lEffectiveChars.push( lLine.charAt(0).toUpperCase() ) ;
        lEffectiveChars.push( lMiddleChar );
        lEffectiveChars.push( lLine.charAt(2).toLowerCase() ) ;
    }

    if (lEffectiveChars.length == 0 )
    {
      console.log("    Monogram is empty");
      return resolve({ figures: [], anchor: new Point(0,0) });
    }

    this._effectiveText = lEffectiveChars.join("");
    console.log("    EffectiveText: " + this._effectiveText);

    console.log(`    FrameSystem: ${this.frameSystem}`);
    console.log(`    FontStyle: ${this.fontStyle}`);

    var lEffectiveFontFullName = `Stahls Mgram ${this.frameSystem} ${this.fontStyle} ${lEffectiveChars.length} LTR`;
    console.log("    Effective Font Fullname: " + lEffectiveFontFullName);

    this._effectiveFont = new Font({ fullName: lEffectiveFontFullName });

    return _this.fontData$().then(() => {

      const textLayout = TextLayout(_this);

      const textLines = textLayout.textLines();
      if ( textLines == null || textLines.length === 0 || (textLines.length === 1 && textLines[0]._chars.length === 0)) {
        console.log("    no chars!");
        return { figures: [], anchor: new Point(0,0) };
      }

      // Keep a reference to the textLayout, we are going to use it in the tools
      _this._textLayout = textLayout;

      return { figures: [textLayout], anchor: new Point(0,0) };
    });
  },

  // internal

  _clone() {

    console.log('MONOGRAM clone()');
    const p = this.callParent();
    return p;
  },

  clone(includingProcessor, partOfGroup, ungrouping) {

    console.log(`MONOGRAM _clone(includingProcessor:${includingProcessor},partOfGroup:${partOfGroup},ungrouping:${ungrouping})`);

    const t = this.callParent(includingProcessor, partOfGroup, ungrouping);

    const textLayout = this._textLayout;
    if (textLayout) {
      t._textLayout = this._textLayout.clone();
      t._textLayout._text = t;
    }

    t._fontData      = this._fontData;
    t._fontDataScale = this._fontDataScale;
    t._fontDataLines = this._fontDataLines;
    t._effectiveText = this._effectiveText;
    t._effectiveFont = this._effectiveFont;

    return t;
  },

  outputsText() {
    return true;
  },

  init(p = {}) {

    console.log("MONOGRAM init: " + p );

    this.callParent(p);

    // Used while generating the outputFigure, kept for internal tools that
    // modifies the text layout without a full reprocessing (for example
    // CharsTransform)
    this._textLayout    = null;
    this._fontData      = null;
    this._fontDataScale = null;
    this._fontDataLines = null;

    this.extraCharSpacing = new Magnitude(0,'mm') ;
    this.extraWordSpacing = new Magnitude(0,'mm') ;
    this.columnSpacing    = new Magnitude(0,'mm') ;
    this.linesAlign       = 'Left';

    this._effectiveText = "";
    this._effectiveFont = "";

    if ( p.brush === undefined) {
      this.brush = new Brush({ color: Color.Black() });
    }

    this.anchor = new AnchorGenerator({ pos: new Point(0, 0), absolute: true });

  },

  needsCacheToRender() {
    if (Config.alwaysRenderLocally) { return false; }
    if (!Config.renderLocallyWhenPossible) { return true; }
    return !this.powerClipsLocalRendering();
  },

  hitTest(point, options = {}) {
    options.regionFrames = Config.useCharactersBoundsBasedSelection;
    if (this.canBeSelected() && this.globalFrame_().contains(point)) { // [TODO] 'selectable'
      if (options.frameBased || !Config.useCurvesBasedSelection) {
        return true;
      }
      return this._processor.fineHitTest(point, this.globalMatrix_(), options);
    }
    return false;
  },

  // [TODO] Review
  spaceWidth_         () { return this._fontData.spaceWidth         (this._fontDataScale); },
  ascent_             () { return this._fontData.ascent             (this._fontDataScale); },
  descent_            () { return this._fontData.descent            (this._fontDataScale); },
  sizeOfReferenceChar_() { return this._fontData.sizeOfReferenceChar(this._fontDataScale); },

  isWidthwise               ()            { return true; },
  doMultilineVerticalText   ()            { return false ; },
  effectiveLineDirection    ()            { return new Point(1,0); },
  charDirectionVector       ()            { return new Point(0,1); },
  effectiveExtraLineSpacing_()            { return 0 ; },
  effectiveLinesPerColumn   ( charsCount) { return 1 ; },
  correctZeroAdvance        ()            { return false ; },
  



});

Monogram.parseXml = function (node, bcFixes) {

  const monogram = new Monogram();

  Figure._baseParseXml.call(monogram, node, Monogram, bcFixes);

  return monogram;
};

/*

 _fontDataLines
 */



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/monogram.js
 **/